import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import App from './App';

// Create a client with optimized caching strategies
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Global defaults - conservative for data integrity
      staleTime: 1000 * 60 * 2, // 2 minutes default
      gcTime: 1000 * 60 * 30, // 30 minutes garbage collection
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
      
      // Retry logic with exponential backoff
      retry: (failureCount, error: any) => {
        // Don't retry on authentication errors (401, 403)
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          return false;
        }
        // Don't retry on client errors (4xx)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 3 times for server errors and network issues
        return failureCount < 3;
      },
      
      // Exponential backoff for retries
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Network mode - fail immediately if offline
      networkMode: 'online',
    },
    mutations: {
      // Optimistic updates and error handling
      retry: false, // Don't retry mutations automatically
      onError: (error: any) => {
        // Global error handling for mutations
        console.error('Mutation error:', error);
      },
    },
  },
});

// Ant Design theme configuration
const theme = {
  token: {
    colorPrimary: '#169959', // Using design system positive color
    fontFamily: '"Reddit Sans", sans-serif',
    borderRadius: 8,
  },
  components: {
    Layout: {
      headerBg: '#ffffff',
      bodyBg: '#f5f5f5',
    },
  },
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ConfigProvider theme={theme}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ConfigProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);